import React, { FC, useState } from 'react';
import { BiPlusCircle, BiX } from 'react-icons/bi';
import { twMerge } from 'tailwind-merge';

import RenderIf from '@/components/Common/RenderIf';
import Button, {
  ButtonVariants,
} from '@/components/shared/basic/button/button';

import { Address } from '@/utils/interfaces/Manage-Addresses';

interface Props {
  onCancel?:
  | ((e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)
  | undefined;
  addresses: Address[] | undefined;
  onSelect?: (e: Address) => void;
  selectedAddress?: Address | null;
  onClickCreate?: () => void;
  onClickEdit?: (address: Address) => void;
  varient?: 'sheet' | 'modal';
  isSelectButton?: boolean | undefined;
  isBillingButton?: boolean;
  /** When true, renders a "Set as Default" button for each address row */
  showDefaultButton?: boolean;
  /** Called when user clicks "Set as Default"; should return a Promise */
  onSetDefault?: (address: Address) => Promise<void>;
}

const BaseModal: FC<Props> = ({
  onCancel,
  addresses,
  onSelect,
  selectedAddress,
  onClickCreate,
  onClickEdit,
  varient,
  isSelectButton = true,
  isBillingButton = true,
  showDefaultButton = false,
  onSetDefault,
}) => {
  const [defaultLoadingId, setDefaultLoadingId] = useState<number | null>(null);

  const handleSetDefault = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    address: Address,
  ) => {
    e.stopPropagation();
    if (!onSetDefault) return;
    setDefaultLoadingId(address.id);
    try {
      await onSetDefault(address);
    } finally {
      setDefaultLoadingId(null);
    }
  };

  return (
    <>
      <div
        className={twMerge(
          'py-4 flex items-center justify-between px-4',
          varient === 'sheet' ? 'sticky top-0 bg-grayZl-950 z-40' : '',
        )}
      >
        <div className='invisible w-6 h-6'></div>
        <p className='text-grayZl-25 md:text-base text-sm/[22px] font-bold'>
          Select Address
        </p>
        <button onClick={onCancel}>
          <BiX className='w-6 h-6 text-white rounded-full bg-white/10' />
        </button>
      </div>
      <div className='p-4 bg-stroke flex items-center justify-between'>
        <p className='uppercase text-grayZl-700 font-bold tracking-[15%] md:text-sm text-xs'>
          Saved Address
        </p>
        <button className='flex items-center justify-center gap-1'>
          <BiPlusCircle className='text-primary md:w-[18px] w-[16px] md:h-[18px] h-[16px]' />
          <p
            onClick={() => onClickCreate?.()}
            className='text-primary md:text-sm text-xs/5 font-semibold'
          >
            Add New Address
          </p>
        </button>
      </div>
      <div className='max-h-[70vh] overflow-auto zai-scrollbar '>
        {addresses?.map((address) => (
          <div
            onClick={() => {
              onSelect?.(address);
              if (!isSelectButton) {
                onCancel?.();
              }
            }}
            key={address?.id}
            className='p-4  flex items-start gap-2'
          >
            <div className='w-4 h-4 border-white border-[2px] rounded-full relative mt-0.5'>
              <div className='absolute top-0 left-0 w-full h-full flex items-center justify-center'>
                <RenderIf condition={selectedAddress?.id === address.id}>
                  <div className='w-2 h-2 bg-white absolute  rounded-full'></div>
                </RenderIf>
              </div>
            </div>
            <div className=' flex-1'>
              <div className='flex items-center gap-1'>
                <p className='text-white font-xs font-bold'>
                  {address?.fullname}
                </p>
                <RenderIf condition={!!address?.set_default}>
                  <p className='text-[10px]/[18px] text-grayZl-600'>
                    (Default)
                  </p>
                </RenderIf>
              </div>
              <p className='text-grayZl-500 md:text-xs/[20px] text-[10px]/[18px]'>
                {address?.address}
              </p>
              <p className='text-grayZl-500 md:text-xs/[20px] text-[10px]/[18px]'>
                {(address?.city || address?.city_name) +
                  ', ' +
                  (address?.state || address?.state_name) +
                  ' - ' +
                  (address?.postalcode || address?.postal_code)}
              </p>
              <p className='text-grayZl-500 md:text-xs/[20px] text-[10px]/[18px]'>
                {address?.country_name || address?.country}
              </p>
              <p className='text-grayZl-500 md:text-xs/[20px] text-[10px]/[18px]'>
                Mobile: {'+' + address?.country_code + ' ' + address?.phone}
              </p>
              <RenderIf
                condition={
                  !isSelectButton || selectedAddress?.id === address?.id
                }
              >
                <div
                  className={`mt-3 flex items-center 
                  ${isBillingButton || showDefaultButton ? 'justify-between' : 'justify-end'}
                  `}
                >
                  <RenderIf condition={isBillingButton}>
                    <Button
                      disabled
                      variant={ButtonVariants.UNSTYLED}
                      size={0}
                      className='text-grayZl-700 bg-grayZl-900 md:px-4 px-3 py-1.5 rounded-lg font-semibold md:text-xs/5 text-[10px]/[18px]'
                    >
                      Billing Address
                    </Button>
                  </RenderIf>
                  <div className='flex items-center gap-2'>
                    <RenderIf condition={showDefaultButton && !address?.set_default}>
                      <Button
                        onClick={(e) => handleSetDefault(e, address)}
                        disabled={defaultLoadingId === address.id}
                        variant={ButtonVariants.UNSTYLED}
                        size={0}
                        className='text-white border border-white/40 bg-white/10 px-3 py-1.5 rounded-lg font-semibold md:text-xs/5 text-[10px]/[18px] flex items-center gap-1 disabled:opacity-60'
                      >
                        {defaultLoadingId === address.id ? (
                          <>
                            <span className='w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block' />
                            Setting…
                          </>
                        ) : (
                          'Set as Default'
                        )}
                      </Button>
                    </RenderIf>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickEdit?.(address);
                      }}
                      variant={ButtonVariants.UNSTYLED}
                      size={0}
                      className='text-white bg-grayZl-900 px-3 py-1.5 rounded-lg font-semibold md:text-xs/5 text-[10px]/[18px]'
                    >
                      Edit
                    </Button>
                    {/* <Button
                      variant={ButtonVariants.UNSTYLED}
                      size={0}
                      className='text-white border border-grayZl-900 md:px-3 px-1.5 py-1.5 md:py-1.5 rounded-lg font-semibold'
                    >
                      <BiTrash className='text-white md:w-5 w-[18px] md:h-5 h-[18px]' />
                    </Button> */}
                  </div>
                </div>
              </RenderIf>
            </div>
          </div>
        ))}
      </div>
      <RenderIf condition={isSelectButton}>
        <div
          className={twMerge(
            'border-t border-grayZl-900 py-5 px-4',
            varient === 'sheet'
              ? 'sticky bottom-0 border-grayZl-900 bg-grayZl-950'
              : '',
          )}
        >
          <Button
            type='button'
            onClick={() => onCancel?.()}
            variant={ButtonVariants.UNSTYLED}
            size={0}
            className='text-white bg-grayZl-900 px-4 py-[9px] rounded-lg font-semibold text-sm w-full'
          >
            Select Address
          </Button>
        </div>
      </RenderIf>
    </>
  );
};

export default BaseModal;
